import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ServiceAuthClient from './ServiceAuthClient';
import ServiceAuthContext from './ServiceAuthContext';
import { initialAuthState } from './initialAuthState';
import { hasAuthParams } from './ServiceAuthUtils';

const SS_SA_CODE_VERIFIER = 'pat.serviceauth.codeVerifier';
const SS_SA_STATE = 'pat.serviceauth.state';
const SS_RETURN = 'pat.serviceauth.return';

const reducer = (state, action) => {
  // console.log('In reducer...');
  // console.log('State: ', state);
  // console.log('Action: ', action);

  switch (action.type) {
    case 'INITIALISED':
      return {
        ...state,
        isAuthenticated: !!action.user,
        user: action.user,
        error: undefined,
        isLoading: false,
      };
    case 'HANDLE_REDIRECT_COMPLETE':
    case 'GET_ACCESS_TOKEN_COMPLETE':
      return {
        ...state,
        isAuthenticated: !!action.user,
        user: action.user,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: undefined,
      };
    case 'ERROR':
    default:
      return {
        ...state,
        error: action.error,
        isLoading: false,
      };
  }
};

const ServiceAuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [client] = useState(() => new ServiceAuthClient());
  // console.log(client);
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  // console.log(state);

  useEffect(() => {
    (async () => {
      console.log('[ServiceAuthProvider] Initializing auth...');
      try {
        if (hasAuthParams()) {
          console.log(
            '[ServiceAuthProvider] Found auth params in URL - handling redirect callback'
          );
          const { returnTo } = await client.handleRedirectCallback();

          console.log(
            '[ServiceAuthProvider] Redirect callback complete, returning to:',
            returnTo
          );
          navigate(returnTo);
        } else {
          console.log(
            '[ServiceAuthProvider] No auth params - checking existing session'
          );
          await client.checkSession();
        }
        const user = await client.getUser();
        console.log(
          '[ServiceAuthProvider] User retrieved:',
          user ? 'authenticated' : 'not authenticated'
        );
        // @ts-ignore
        localStorage.removeItem(SS_SA_STATE);
        localStorage.removeItem(SS_SA_CODE_VERIFIER);
        localStorage.removeItem(SS_RETURN);
        dispatch({ type: 'INITIALISED', user });
      } catch (error) {
        console.error(
          '[ServiceAuthProvider] Error during initialization:',
          error
        );
        // @ts-ignore
        dispatch({ type: 'ERROR', error: error });
      }
    })();
  }, [navigate, client]);

  const redirectToServiceauth = useCallback(
    (opts) => client.redirectToServiceauth(),
    [client]
  );

  const logout = useCallback(
    (opts) => {
      client.logout();

      navigate(
        `/login?severity=success&message=${encodeURIComponent(
          'You have been signed out.'
        )}`
      );

      // @ts-ignore
      dispatch({ type: 'LOGOUT' });
    },
    [navigate, client]
  );

  const getAccessToken = useCallback(
    async (opts) => {
      // console.log('Options:', opts);

      let token;

      try {
        token = await client.getAccessToken(opts);
        // console.log("Got token: " + token);
      } catch (error) {
        console.log('Error when getting token: ' + error);
      } finally {
        // @ts-ignore
        dispatch({
          type: 'GET_ACCESS_TOKEN_COMPLETE',
          user: await client.getUser(),
        });
      }

      return token;
    },
    [client]
  );

  return (
    <ServiceAuthContext.Provider
      value={{ ...state, redirectToServiceauth, getAccessToken, logout }}
    >
      {children}
    </ServiceAuthContext.Provider>
  );
};

export default ServiceAuthProvider;
